import React, { forwardRef } from 'react';

import { KeyboardArrowLeft } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grow,
  Slide,
  Zoom,
  Stack,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';

// ------------------------------------------------------------------------------ styles

const DialogHeader = styled(DialogTitle)(({ theme }) => ({
  padding: '24px',
  color: theme.palette.neutral100.main,
  ...theme.typography.subheading1,
  [theme.breakpoints.down('md')]: {
    padding: '16px',
    ...theme.typography.subheading3,
  },
}));

const Breadcrumb = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '1.25rem',
}));

const BreadcrumbIcon = styled(KeyboardArrowLeft)(({ theme }) => ({
  color: theme.palette.grey[4],
  cursor: 'pointer',
  marginRight: '.5rem',
}));

const BreadcrumbText = styled(Box)(({ theme }) => ({
  color: theme.palette.grey[1],
  fontSize: '16px',
  fontWeight: 900,
  lineHeight: '20px',
}));

// ------------------------------------------------------------------------------ components

const slide = forwardRef((props, ref) => {
  return <Slide direction="down" ref={ref} {...props} />;
});
const grow = forwardRef((props, ref) => {
  return <Grow in ref={ref} {...props} />;
});
const zoom = forwardRef((props, ref) => {
  return <Zoom in style={{ transitionDelay: '100ms' }} ref={ref} {...props} />;
});

const DialogBreadcrumb = ({ title, onClick: handleBack }) => {
  return (
    <Breadcrumb>
      <BreadcrumbIcon onClick={handleBack} />
      <BreadcrumbText>{title}</BreadcrumbText>
    </Breadcrumb>
  );
};

const PWDKDialog = ({
  open,
  onClose,
  onlyCloseButton,
  title,
  subtitle,
  transition,
  maxWidth,
  fullWidth,
  verticalAlign,
  blank,
  headerStyle,
  contentStyle,
  children,
  paperPropsStyle,
  dialogActions,
  actionStyle,
  sx,
  ...other
}) => {
  const transitions = {
    slide,
    grow,
    zoom,
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={transitions[transition || 'fade']}
      maxWidth={maxWidth || 'sm'}
      fullWidth={fullWidth}
      verticalAlign={verticalAlign}
      PaperProps={{
        style: { borderRadius: '8px', ...paperPropsStyle },
      }}
      sx={{
        '& .MuiDialog-scrollPaper': {
          alignItems: verticalAlign,
        },
        ...sx,
      }}
      {...other}
    >
      <DialogHeader sx={{ ...headerStyle }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          {title}
          {onClose && (
            <CloseIcon
              sx={{
                marginLeft: 'auto',
                fontSize: '24px',
                color: 'neutral400.main',
                cursor: 'pointer',
              }}
              onClick={onClose || onlyCloseButton}
            />
          )}
        </Stack>
        {subtitle && (
          <Typography
            typography={{ md: 'paragraph2', xs: 'captionreguler' }}
            color="neutral400.main"
            mt={0.5}
          >
            {subtitle}
          </Typography>
        )}
      </DialogHeader>
      {!blank && <Divider />}
      <DialogContent sx={{ p: { md: '24px', xs: '16px' }, ...contentStyle }}>
        {children}
      </DialogContent>
      {dialogActions && (
        <>
          {!blank && <Divider />}
          <DialogActions sx={{ p: { md: '24px', xs: '16px' }, ...actionStyle }}>
            {dialogActions}
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default PWDKDialog;
export { DialogBreadcrumb };
